/**
 * Variant matrix SKU authorship.
 *
 * Operator-authored SKUs are canonical catalogue identity. Rebuilding the
 * matrix must not silently rename them, and no SKU-rewriting action may run
 * while those SKUs are locked.
 *
 * Rules:
 *   - a SKU the operator typed becomes "manual" and locks the matrix;
 *   - any action that replaces SKUs needs an explicit unlock;
 *   - any action that replaces an already-generated matrix needs confirmation;
 *   - regenerating preserves the operator's non-SKU work (price, cost,
 *     quantities, barcode, dimensions, inclusion, status) for combinations
 *     whose option values still exist.
 */

import type { CombinationRow } from "@/lib/products";

/** Stable identity of one matrix row: its option values, not its position. */
export function combinationKey(values: string[]): string {
  return values
    .map((value) => String(value ?? "").trim().toLowerCase())
    .join("|");
}

export function markManualSku(keys: Set<string>, values: string[]): Set<string> {
  const next = new Set(keys);
  next.add(combinationKey(values));
  return next;
}

export function unmarkManualSku(keys: Set<string>, values: string[]): Set<string> {
  const next = new Set(keys);
  next.delete(combinationKey(values));
  return next;
}

/** Drop locks whose combination no longer exists, so stale keys cannot block work. */
export function pruneManualSkuKeys(keys: Set<string>, rows: { values: string[] }[]): Set<string> {
  const live = new Set(rows.map((row) => combinationKey(row.values)));
  const next = new Set<string>();
  for (const key of keys) {
    if (live.has(key)) next.add(key);
  }
  return next;
}

/** SKU values to persist with the draft so a resume can restore the locks. */
export function manualSkuValues(rows: { values: string[]; sku: string }[], keys: Set<string>): string[] {
  return rows
    .filter((row) => keys.has(combinationKey(row.values)))
    .map((row) => row.sku);
}

/** Rebuild the lock set from a resumed draft payload. */
export function manualKeysFromPayload(rows: { values: string[]; sku: string }[], manualSkus: string[] | null | undefined): Set<string> {
  const wanted = new Set((manualSkus ?? []).map((sku) => String(sku ?? "").trim().toUpperCase()).filter(Boolean));
  const keys = new Set<string>();
  if (wanted.size === 0) return keys;
  for (const row of rows) {
    if (wanted.has(String(row.sku ?? "").trim().toUpperCase())) keys.add(combinationKey(row.values));
  }
  return keys;
}

/**
 * Restore the SKU pattern with the draft. A resumed draft without a stored
 * pattern keeps the editor default; a stored blank pattern is ignored.
 */
export function restoredSkuPattern(pattern: string | null | undefined): string | null {
  const value = typeof pattern === "string" ? pattern.trim() : "";
  return value.length > 0 ? value : null;
}

export function isSkuMatrixLocked(manualCount: number): boolean {
  return manualCount > 0;
}

export interface SkuLockSummary {
  manualCount: number;
  totalCount: number;
  locked: boolean;
  label: string;
}

export function skuLockSummary(manualCount: number, totalCount: number): SkuLockSummary {
  const locked = isSkuMatrixLocked(manualCount);
  if (!locked) {
    return {
      manualCount: 0,
      totalCount,
      locked: false,
      label: totalCount > 0 ? "All SKUs are generated from the pattern" : "No SKUs generated yet",
    };
  }
  return {
    manualCount,
    totalCount,
    locked: true,
    label: `${manualCount.toLocaleString()} of ${totalCount.toLocaleString()} SKU${totalCount === 1 ? "" : "s"} manually edited and locked`,
  };
}

export type SkuRewriteAction = "regenerate" | "pattern" | "prefix_suffix";

export interface SkuRewriteGuardInput {
  action: SkuRewriteAction;
  manualCount: number;
  unlocked: boolean;
  hasExistingMatrix: boolean;
  confirmed: boolean;
}

export interface SkuRewriteGuard {
  allowed: boolean;
  needsUnlock: boolean;
  needsConfirm: boolean;
  message: string | null;
}

const ACTION_VERB: Record<SkuRewriteAction, string> = {
  regenerate: "Regenerating combinations",
  pattern: "Applying the SKU pattern",
  prefix_suffix: "Applying a prefix or suffix",
};

/**
 * Two gates before any SKU rewrite: unlock (manual SKUs exist) then confirm
 * (an existing matrix or manual work would be replaced).
 */
export function skuRewriteGuard(input: SkuRewriteGuardInput): SkuRewriteGuard {
  const needsUnlock = isSkuMatrixLocked(input.manualCount) && !input.unlocked;
  if (needsUnlock) {
    const count = input.manualCount.toLocaleString();
    return {
      allowed: false,
      needsUnlock: true,
      needsConfirm: false,
      message: `${count} SKU${input.manualCount === 1 ? " is" : "s are"} manually edited and locked. Unlock SKU editing before changing them.`,
    };
  }

  const needsConfirm = input.hasExistingMatrix || isSkuMatrixLocked(input.manualCount);
  if (needsConfirm && !input.confirmed) {
    const suffix = isSkuMatrixLocked(input.manualCount)
      ? " The unlocked manual SKUs will be replaced."
      : "";
    return {
      allowed: false,
      needsUnlock: false,
      needsConfirm: true,
      message: `${ACTION_VERB[input.action]} will replace the current SKUs.${suffix} Continue?`,
    };
  }

  return { allowed: true, needsUnlock: false, needsConfirm, message: null };
}

export interface MergeRegeneratedInput {
  regenerated: CombinationRow[];
  previous: CombinationRow[];
  manualKeys: Set<string>;
}

/**
 * Fold freshly generated rows over the operator's existing work.
 * Identity is the option values, so reordering options never loses data.
 * Locked SKUs survive verbatim; unlocked SKUs come from the pattern.
 */
export function mergeRegeneratedCombinations(input: MergeRegeneratedInput): CombinationRow[] {
  const previousByKey = new Map<string, CombinationRow>();
  for (const row of input.previous) {
    previousByKey.set(combinationKey(row.values), row);
  }

  return input.regenerated.map((row) => {
    const key = combinationKey(row.values);
    const previous = previousByKey.get(key);
    if (!previous) return row;
    return {
      ...row,
      sku: input.manualKeys.has(key) ? previous.sku : row.sku,
      barcode: previous.barcode,
      price: previous.price,
      cost: previous.cost,
      weightKg: previous.weightKg,
      lengthCm: previous.lengthCm,
      widthCm: previous.widthCm,
      heightCm: previous.heightCm,
      qty: previous.qty ?? null,
      status: previous.status,
      included: previous.included,
    };
  });
}

/**
 * What an operator has actually authored in the current matrix.
 *
 * Removing an option dimension invalidates combinations, so the editor must be
 * able to say whether that removal would destroy real work — not merely rows
 * that were generated and never touched.
 */
export interface MatrixAuthoredSummary {
  rows: number;
  excludedRows: number;
  manualSkus: number;
  editedRows: number;
  hasOperatorData: boolean;
}

export function summarizeMatrixAuthoredData(
  rows: CombinationRow[],
  manualKeys: Set<string>,
): MatrixAuthoredSummary {
  let excludedRows = 0;
  let editedRows = 0;
  for (const row of rows) {
    if (!row.included) excludedRows += 1;
    const edited = row.barcode != null
      || row.price != null
      || row.cost != null
      || row.qty != null
      || row.weightKg != null
      || row.lengthCm != null
      || row.widthCm != null
      || row.heightCm != null
      || row.status !== "active";
    if (edited) editedRows += 1;
  }
  return {
    rows: rows.length,
    excludedRows,
    manualSkus: manualKeys.size,
    editedRows,
    hasOperatorData: rows.length > 0
      && (excludedRows > 0 || editedRows > 0 || manualKeys.size > 0),
  };
}

export interface OptionRemovalDecision {
  allowed: boolean;
  needsConfirm: boolean;
  message: string;
}

/**
 * Gate the destructive option-dimension removal. Cancel must leave every row,
 * quantity, SKU and lock untouched — the caller only clears state after an
 * explicit confirmation.
 */
export function optionRemovalDecision(opts: {
  summary: MatrixAuthoredSummary;
  confirmed: boolean;
}): OptionRemovalDecision {
  if (!opts.summary.hasOperatorData) {
    return { allowed: true, needsConfirm: false, message: "" };
  }
  const lost: string[] = [];
  if (opts.summary.rows > 0) {
    lost.push(`${opts.summary.rows.toLocaleString()} variant row${opts.summary.rows === 1 ? "" : "s"}`);
  }
  if (opts.summary.manualSkus > 0) {
    lost.push(`${opts.summary.manualSkus.toLocaleString()} manually edited SKU${opts.summary.manualSkus === 1 ? "" : "s"}`);
  }
  if (opts.summary.excludedRows > 0) {
    lost.push(`${opts.summary.excludedRows.toLocaleString()} excluded combination${opts.summary.excludedRows === 1 ? "" : "s"}`);
  }
  if (opts.summary.editedRows > 0) {
    lost.push(`entered prices, quantities or dimensions on ${opts.summary.editedRows.toLocaleString()} row${opts.summary.editedRows === 1 ? "" : "s"}`);
  }
  return {
    allowed: opts.confirmed,
    needsConfirm: true,
    message: `Removing this option dimension will discard ${lost.join(", ")}. This cannot be undone. Remove it?`,
  };
}